<!-- FirstAccessView.vue -->

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useUserStore } from "@/stores/useUserStore";
import { trackFirstAccessView, trackFirstAccessChoice } from "@/utils/tracking";
import { useFounderContact } from "@/composables/useFounderContact";

const router = useRouter();
const userStore = useUserStore();

const isLoading = ref(false);

// Conta nova vê primeiro a mensagem do fundador, dentro deste mesmo cartão;
// qualquer um dos dois botões a dispensa e as opções de início aparecem.
const {
  founderWhatsAppUrl,
  founderFirstName,
  founderWelcomePending,
  dismissFounderWelcome,
} = useFounderContact();

async function handleChoice(routeName, markComplete, state = {}, choice = null) {
  if (isLoading.value) return;
  isLoading.value = true;

  // O evento só é enviado depois que o onboarding é persistido: se a gravação
  // falhar, o usuário volta a esta tela no próximo login e escolherá de novo —
  // registrar a escolha aqui contaria um onboarding que não aconteceu.
  let onboardingPersisted = !markComplete;

  if (markComplete) {
    try {
      await userStore.markOnboardingAsComplete();
      onboardingPersisted = true;
    } catch (err) {
      console.error("[FirstAccessView] Falha ao marcar onboarding:", err);
    }
  }

  if (choice && onboardingPersisted) {
    trackFirstAccessChoice(choice);
  }

  router.push({ name: routeName, state });
}

onMounted(() => {
  trackFirstAccessView();
});
</script>

<template>
  <div class="first-access-wrapper">
    <div class="first-access-card">
      <div v-if="founderWelcomePending" class="founder-step">
        <h1 class="first-access-title">
          {{ founderFirstName ? `Olá, ${founderFirstName}!` : "Olá!" }}
        </h1>

        <p class="founder-step__body">
          Sou o Edson, criei o Litterae. Se travar em algo ou tiver uma ideia,
          me chama direto aqui:
        </p>

        <div class="founder-step__actions">
          <a
            :href="founderWhatsAppUrl"
            target="_blank"
            rel="noopener"
            class="btn btn-whatsapp"
            @click="dismissFounderWelcome"
          >
            <i class="fab fa-whatsapp" aria-hidden="true"></i>
            Chamar no WhatsApp
          </a>
          <button
            type="button"
            class="btn btn-secondary"
            @click="dismissFounderWelcome"
          >
            Agora não
          </button>
        </div>

        <p class="founder-step__hint">
          Depois, é só usar o ícone
          <i class="fab fa-whatsapp" aria-hidden="true"></i>
          no topo da tela ou o menu da sua foto.
        </p>
      </div>

      <template v-else>
      <div class="first-access-header">
        <h1 class="first-access-title">Como você quer começar no Litterae?</h1>
        <p class="first-access-subtitle">Escolha um ponto de partida. Você pode mudar de ideia a qualquer momento pelo menu lateral.</p>
      </div>

      <div class="first-access-options">
        <button
          class="choice-btn"
          :disabled="isLoading"
          @click="handleChoice('TextCardsForm', true, {}, 'create_own_text')"
        >
          <span class="choice-btn__icon"><i class="fas fa-book"></i></span>
          <span class="choice-btn__text">
            <strong>Cadastrar meu próprio texto</strong>
            <small>Adicione um texto para anotar e estudar</small>
          </span>
          <i class="fas fa-chevron-right choice-btn__arrow"></i>
        </button>

        <button
          class="choice-btn"
          :disabled="isLoading"
          @click="handleChoice('TextForm', true, {}, 'explore_texts')"
        >
          <span class="choice-btn__icon"><i class="fas fa-search"></i></span>
          <span class="choice-btn__text">
            <strong>Explorar textos disponíveis</strong>
            <small>Veja os textos já cadastrados na plataforma</small>
          </span>
          <i class="fas fa-chevron-right choice-btn__arrow"></i>
        </button>

        <button
          class="choice-btn"
          :disabled="isLoading"
          @click="handleChoice('ActivityGeneralForm', true, {}, 'view_activities')"
        >
          <span class="choice-btn__icon"><i class="fas fa-tasks"></i></span>
          <span class="choice-btn__text">
            <strong>Ver atividades disponíveis</strong>
            <small>Explore atividades de leitura e anotação</small>
          </span>
          <i class="fas fa-chevron-right choice-btn__arrow"></i>
        </button>

        <button
          class="choice-btn choice-btn--subtle"
          :disabled="isLoading"
          @click="handleChoice('DashboardHome', true, {}, 'dashboard')"
        >
          <span class="choice-btn__icon"><i class="fas fa-th-large"></i></span>
          <span class="choice-btn__text">
            <strong>Ir para o painel geral</strong>
            <small>Acesse o dashboard e explore por conta própria</small>
          </span>
          <i class="fas fa-chevron-right choice-btn__arrow"></i>
        </button>
      </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.first-access-wrapper {
  display: flex;
  align-items: flex-start; 
  justify-content: center;
  min-height: 100%;
  padding: 2rem 1rem;
  padding-top: 4em;
}

.first-access-card {
  width: 100%;
  max-width: 560px;
  background: var(--surface);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Mensagem do fundador, primeira etapa do cartão para conta nova */
.founder-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
}

.founder-step__body {
  font-size: 0.95rem;
  color: var(--text-dark);
  line-height: 1.6;
  margin: 0;
  max-width: 420px;
}

.founder-step__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.65rem;
}

.founder-step__hint {
  font-size: var(--text-md);
  color: var(--text-light);
  margin: 0;
}

.first-access-header {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
}

.first-access-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
  line-height: 1.3;
}

.first-access-subtitle {
  font-size: 0.875rem;
  color: var(--text-light);
  margin: 0;
  max-width: 400px;
}

.first-access-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.choice-btn {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.1rem;
  border-radius: 14px;
  border: 1.5px solid var(--border-color);
  background: #fefbf5;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease, transform 0.08s ease;
}

.choice-btn:hover:not(:disabled) {
  border-color: var(--brand-light);
  background: #f5f3ff;
  box-shadow: 0 4px 12px rgba(189, 178, 255, 0.2);
}

.choice-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.choice-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.choice-btn:focus,
.choice-btn:focus-visible {
  outline: none;
  border-color: var(--brand-light);
  box-shadow: var(--focus-ring-brand);
}

.choice-btn--subtle {
  background: #f9f9f9;
}

.choice-btn--subtle:hover:not(:disabled) {
  background: var(--surface-hover);
  border-color: #c0c0c0;
  box-shadow: none;
}

.choice-btn__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 10px;
  background: #f0edff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: var(--text-brand);
}

.choice-btn--subtle .choice-btn__icon {
  background: #ebebeb;
  color: var(--text-light);
}

.choice-btn__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.choice-btn__text strong {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--text-dark);
  line-height: 1.3;
}

.choice-btn__text small {
  font-size: 0.78rem;
  color: var(--text-light);
  font-weight: 400;
  line-height: 1.3;
}

.choice-btn__arrow {
  font-size: 0.75rem;
  color: #c0c0c0;
  flex-shrink: 0;
  transition: color 0.18s ease;
}

.choice-btn:hover:not(:disabled) .choice-btn__arrow {
  color: var(--brand);
}
</style>
